import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Download, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const QUESTIONS = [
  {
    key: "intention",
    q: "What draws you to a retreat right now?",
    options: ["Deep rest & recovery", "Emotional release", "A creative spark", "A physical reset"],
  },
  {
    key: "feeling",
    q: "How do you feel most days lately?",
    options: ["Overwhelmed", "Disconnected", "Restless", "Curious & open"],
  },
  {
    key: "experience",
    q: "Which experience calls to you most?",
    options: ["Sound healing", "Guided yoga", "Meditation & breathwork", "Nature immersion"],
  },
  {
    key: "time",
    q: "How much time can you give yourself?",
    options: ["A single day", "A weekend", "A full week"],
  },
  {
    key: "transform",
    q: "What would make this truly transformative?",
    options: ["Solitude & silence", "A like-minded community", "Expert guidance", "Room for spontaneity"],
  },
];

export const Quiz = () => {
  const [step, setStep] = useState(0); // 0..QUESTIONS.length -> details, then result
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const total = QUESTIONS.length;
  const isDetails = step === total;
  const progress = result ? 100 : Math.round((step / (total + 1)) * 100);

  const choose = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/quiz/submit", { ...form, answers });
      setResult(data);
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const downloadTip = () => {
    const text = `Your Daily Wellness Ritual — Sound Body Retreat\nFor: ${form.name}\n\n${result.wellness_tip}\n`;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "daily-wellness-ritual.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section id="quiz" className="py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-clay mb-4">The wellness quiz</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-ink">
            Discover your retreat & a free daily ritual
          </h2>
          <p className="mt-5 text-muted">
            Five gentle questions. We'll craft a personalized wellness ritual you can keep.
          </p>
        </div>

        <div className="bg-surface rounded-[2rem] p-7 sm:p-12 min-h-[26rem] flex flex-col">
          {/* progress */}
          <div className="mb-10">
            <div className="h-[3px] bg-line rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-forest"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          <AnimatePresence mode="wait">
            {/* Questions */}
            {!result && !isDetails && (
              <motion.div
                key={`q-${step}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="flex-1 flex flex-col"
              >
                <span className="text-sm text-clay mb-4">
                  {String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl text-ink mb-8 leading-snug">
                  {QUESTIONS[step].q}
                </h3>
                <div className="grid gap-3">
                  {QUESTIONS[step].options.map((opt) => (
                    <button
                      key={opt}
                      data-testid={`quiz-option-${step}-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => choose(QUESTIONS[step].key, opt)}
                      className={`text-left px-6 py-4 rounded-xl border transition-all ${
                        answers[QUESTIONS[step].key] === opt
                          ? "border-forest bg-forest text-sand"
                          : "border-line bg-sand hover:border-forest/50"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    data-testid="quiz-back-btn"
                    className="mt-8 inline-flex items-center gap-2 text-sm text-muted hover:text-forest self-start"
                  >
                    <ArrowLeft size={15} /> Back
                  </button>
                )}
              </motion.div>
            )}

            {/* Details capture */}
            {!result && isDetails && (
              <motion.div
                key="details"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45 }}
                className="flex-1 flex flex-col"
              >
                <h3 className="font-serif text-2xl sm:text-3xl text-ink mb-3 leading-snug">
                  Almost there — where shall we send your ritual?
                </h3>
                <p className="text-muted mb-8">We'll reveal your matched retreat and a personalized daily practice.</p>
                <div className="space-y-6">
                  <input
                    data-testid="quiz-name-input"
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-transparent border-b border-line py-3 text-lg focus:outline-none focus:border-forest transition-colors"
                  />
                  <input
                    data-testid="quiz-email-input"
                    type="email"
                    placeholder="Your email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-transparent border-b border-line py-3 text-lg focus:outline-none focus:border-forest transition-colors"
                  />
                </div>
                <div className="mt-10 flex items-center gap-4">
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    data-testid="quiz-details-back-btn"
                    className="inline-flex items-center gap-2 text-sm text-muted hover:text-forest"
                  >
                    <ArrowLeft size={15} /> Back
                  </button>
                  <button
                    onClick={submit}
                    disabled={loading}
                    data-testid="quiz-submit-btn"
                    className="ml-auto rounded-full bg-forest text-sand px-8 py-4 text-sm hover:bg-forest-deep transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Crafting your ritual…
                      </>
                    ) : (
                      <>
                        Reveal my ritual <Sparkles size={16} />
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Result */}
            {result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex-1"
                data-testid="quiz-result"
              >
                <div className="flex items-center gap-2 text-clay mb-3">
                  <Sparkles size={16} />
                  <span className="text-xs tracking-[0.3em] uppercase">Your free daily ritual</span>
                </div>
                <p className="font-serif text-xl sm:text-2xl text-ink leading-relaxed whitespace-pre-line">
                  {result.wellness_tip}
                </p>

                <div className="mt-8 p-6 rounded-2xl bg-sand border border-line">
                  <p className="text-sm text-muted">We recommend</p>
                  <p className="font-serif text-2xl text-forest mt-1">{result.recommended_package.name}</p>
                  <p className="text-muted text-sm mt-1">{result.recommended_package.tagline}</p>
                </div>

                <div className="mt-8 flex flex-wrap gap-4">
                  <button
                    onClick={downloadTip}
                    data-testid="download-ritual-btn"
                    className="rounded-full bg-forest text-sand px-7 py-3.5 text-sm hover:bg-forest-deep transition-colors inline-flex items-center gap-2"
                  >
                    <Download size={16} /> Download ritual
                  </button>
                  <a
                    href="#retreats"
                    data-testid="result-view-retreat-btn"
                    className="rounded-full border border-forest/40 text-forest px-7 py-3.5 text-sm hover:bg-forest hover:text-sand transition-colors inline-flex items-center gap-2"
                  >
                    Explore {result.recommended_package.name} <ArrowRight size={16} />
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
